import { forwardRef, useMemo, useEffect } from "react";
import * as THREE from "three";
import {
  generateArc,
  createArcMesh,
  intersectTwoDistanceCirclesOnSphere,
  isValid2LSLayout,
} from "../3d/utils/layoutUtils";
import { addTextLabel, drawFingerRing } from "../3d/utils/threeUtils";
import { apiConfig } from "../api/api";
import { useSafeTexture } from "../../hooks/useSafeTexture";

const ballRadius = 4.294;
const arcDistancePinToMB = 6.75;
const arcOffset = 0.01;
const sideLength = 1;

export const BowlingLayout2LS = forwardRef((props, ref) => {
  const {
    pinToPapDistance,
    mbToPapDistance,
    pinToCenter,
    playerPapOffset,
    spanMeasurement,
    spanMeasurements,
    cameraPosition,
    ballId,
    access_token,
    onTextureStatusChange,
    onLayoutError,
  } = props;

  const handednessFactor = spanMeasurements?.isLeftHanded ? -1 : 1;
  const imageTextureUrl =
    ballId && access_token
      ? `${apiConfig.apiServer}ballDrilled/${ballId}/texture?access_token=${access_token}`
      : null;

  const { texture, isLoading } = useSafeTexture(
    imageTextureUrl,
    onTextureStatusChange,
  );

  // Verificar si playerPapOffset tiene valores numéricos válidos
  const hasValidPapOffset = useMemo(() => {
    const v = playerPapOffset?.vertical;
    const h = playerPapOffset?.horizontal;
    return (
      typeof v === "number" &&
      !isNaN(v) &&
      v > 0 &&
      typeof h === "number" &&
      !isNaN(h) &&
      h > 0
    );
  }, [playerPapOffset]);

  const layoutIsValid = useMemo(
    () =>
      hasValidPapOffset &&
      isValid2LSLayout({
        pinToPapDistance,
        mbToPapDistance,
        pinToCenter,
        playerPapOffset,
      }),
    [
      pinToPapDistance,
      mbToPapDistance,
      pinToCenter,
      playerPapOffset,
      hasValidPapOffset,
    ],
  );

  // Notificar error de layout al padre
  useEffect(() => {
    onLayoutError?.(!layoutIsValid);
  }, [layoutIsValid, onLayoutError]);

  const ballGeometry = new THREE.SphereGeometry(ballRadius, 256, 256);
  const ballMaterial = new THREE.MeshStandardMaterial({
    map: texture,
    color: 0xffffff,
    roughness: 0.5,
    metalness: 0.2,
  });
  const ball = new THREE.Mesh(ballGeometry, ballMaterial);

  const makeMarker = (pos, color) => {
    const radius = 0.15;
    const geometry = new THREE.CircleGeometry(radius, 32);
    const material = new THREE.MeshBasicMaterial({
      color,
      side: THREE.DoubleSide,
    });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.copy(pos);
    marker.lookAt(pos.clone().add(pos.clone().normalize()));
    ball.add(marker);
  };

  const pin = new THREE.Vector3(0, ballRadius, 0);
  makeMarker(pin, 0xff0000);

  const axis = new THREE.Vector3(1, 0, 0);
  const thetaPinToMB = arcDistancePinToMB / ballRadius;

  const mb = pin
    .clone()
    .applyAxisAngle(axis, handednessFactor * thetaPinToMB)
    .normalize()
    .multiplyScalar(ballRadius);

  const cg = pin
    .clone()
    .applyAxisAngle(axis, (handednessFactor * thetaPinToMB) / 2)
    .normalize()
    .multiplyScalar(ballRadius);

  const cgNormal = cg.clone().normalize();
  addTextLabel({
    text: "CG",
    position: cg,
    normal: cgNormal,
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  const mbNormalText = mb.clone().normalize();
  addTextLabel({
    text: "PSA",
    position: mb,
    normal: mbNormalText,
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  // Si el layout no es válido, mostrar solo la bola con marcadores básicos
  if (!layoutIsValid) {
    return (
      <group ref={ref} position={[0, 0, 0]}>
        <primitive object={ball} />
      </group>
    );
  }

  const { q1: papCandidate1, q2: papCandidate2 } =
    intersectTwoDistanceCirclesOnSphere(
      pin,
      mb,
      pinToPapDistance,
      mbToPapDistance,
      ballRadius,
    );

  let pap;
  if (papCandidate1 && papCandidate2) {
    if (handednessFactor === 1) {
      pap = papCandidate1.clone();
    } else {
      pap = papCandidate2.clone();
    }
  }

  // Si no se puede calcular PAP, mostrar solo la bola con marcadores básicos
  if (!pap) {
    return (
      <group ref={ref} position={[0, 0, 0]}>
        <primitive object={ball} />
      </group>
    );
  }

  addTextLabel({
    text: "PAP",
    position: pap,
    normal: pap.clone().normalize(),
    ball,
    color: "#ffaa00",
    width: 2,
    lift: 0.05,
  });

  const pinNormal = pin.clone().normalize();
  const arcPointsPinToPap = generateArc({
    center: pap,
    axis: pinNormal,
    radius: ballRadius,
    angleSweep: 2 / ballRadius,
    handednessFactor,
    arcOffset,
  });
  ball.add(createArcMesh(arcPointsPinToPap, 0xffffff));

  const mbNormal = mb.clone().normalize();
  const toPAP = pap.clone().sub(mb).normalize();
  const projectedDirMB = toPAP
    .clone()
    .sub(mbNormal.clone().multiplyScalar(toPAP.dot(mbNormal)))
    .normalize();
  const mbBufferAngle = mbToPapDistance / ballRadius;
  const mbArcCenter = mb
    .clone()
    .applyAxisAngle(
      mbNormal.clone().cross(projectedDirMB).normalize(),
      mbBufferAngle,
    )
    .normalize()
    .multiplyScalar(ballRadius);

  const arcPointsMbToPap = generateArc({
    center: mbArcCenter,
    axis: mbNormal,
    radius: ballRadius,
    angleSweep: 2 / ballRadius,
    handednessFactor,
    arcOffset,
  });
  ball.add(createArcMesh(arcPointsMbToPap, 0xffffff));

  const papDir = pap.clone().normalize();
  const pinDir = pin.clone().normalize();

  let lightningPoint = null;
  let tangentPoint = null;

  const lightningLine =
    Math.acos(
      Math.cos(playerPapOffset.horizontal / ballRadius) *
        Math.cos(playerPapOffset.vertical / ballRadius),
    ) * ballRadius;

  const pinToCenterDistance = Math.abs(pinToCenter);
  const useOppositeLightning = pinToCenter < 0;

  const { q1: lightning1, q2: lightning2 } =
    intersectTwoDistanceCirclesOnSphere(
      pap,
      pin,
      lightningLine,
      pinToCenterDistance,
      ballRadius,
    );

  if (lightning1 && lightning2) {
    const preferFirst = handednessFactor === 1;
    const pickFirst = useOppositeLightning ? !preferFirst : preferFirst;
    lightningPoint = (pickFirst ? lightning1 : lightning2).clone();

    const halfSweep = sideLength / ballRadius;
    const angleSweep = 2 * halfSweep;

    // PAP-centered Lightning mini-arc
    const lightningArcFromPAP = generateArc({
      center: lightningPoint.clone(),
      axis: papDir,
      radius: ballRadius,
      angleSweep,
      segments: 256,
      handednessFactor,
      arcOffset,
    });
    ball.add(createArcMesh(lightningArcFromPAP, 0xffffff));

    const halfSweepPin = sideLength / ballRadius;
    const angleSweepPin = 2 * halfSweepPin;

    const lightningArcFromPin = generateArc({
      center: lightningPoint.clone(),
      axis: pinDir,
      radius: ballRadius,
      angleSweep: angleSweepPin,
      segments: 256,
      handednessFactor,
      arcOffset,
    });
    ball.add(createArcMesh(lightningArcFromPin, 0xffffff));
  }

  let gripCenter = null;
  let gripHorizontalAxis = null;

  if (playerPapOffset.vertical > 0 && playerPapOffset.horizontal > 0) {
    const { q1: t1, q2: t2 } = intersectTwoDistanceCirclesOnSphere(
      pap,
      lightningPoint,
      playerPapOffset.vertical,
      playerPapOffset.horizontal,
      ballRadius,
    );

    if (t1 || t2) {
      const upAxis = new THREE.Vector3(0, 1, 0);
      const score = (pt) => pt.clone().normalize().dot(upAxis);

      if (t1 && t2) {
        const s1 = score(t1);
        const s2 = score(t2);

        if (playerPapOffset.direction === "Up") {
          if (handednessFactor === 1) {
            tangentPoint = t1.clone();
          } else {
            tangentPoint = t2.clone();
          }
        } else {
          if (handednessFactor === 1) {
            tangentPoint = t2.clone();
          } else {
            tangentPoint = t1.clone();
          }
        }
      }
    }

    if (tangentPoint) {
      const angleSweep = 2 * sideLength;

      const papVerticalArcPoints = generateArc({
        center: tangentPoint.clone(),
        axis: papDir,
        radius: ballRadius,
        angleSweep,
        segments: 256,
        handednessFactor,
        arcOffset,
      });
      ball.add(createArcMesh(papVerticalArcPoints, 0xffffff));

      const lightningDir = lightningPoint.clone().normalize();
      const tangentDir = tangentPoint.clone().normalize();
      const axisLT = new THREE.Vector3().crossVectors(lightningDir, tangentDir);

      axisLT.normalize();
      gripHorizontalAxis = axisLT.clone().normalize();
      const totalAngleLT = lightningDir.angleTo(tangentDir);
      const segments = 128;
      const ltPoints = [];

      for (let i = 0; i <= segments; i++) {
        const t = i / segments;
        const theta = totalAngleLT * t;
        const dir = lightningDir.clone().applyAxisAngle(axisLT, theta);
        ltPoints.push(dir.multiplyScalar(ballRadius + arcOffset));
      }

      ball.add(createArcMesh(ltPoints, 0xffffff));

      const axisPT = new THREE.Vector3().crossVectors(papDir, tangentDir);

      axisPT.normalize();
      const totalAnglePT = papDir.angleTo(tangentDir);
      const segmentsPT = 128;
      const papToTangentPoints = [];

      for (let i = 0; i <= segmentsPT; i++) {
        const t = i / segmentsPT;
        const theta = totalAnglePT * t;
        const dir = papDir.clone().applyAxisAngle(axisPT, theta);
        papToTangentPoints.push(dir.multiplyScalar(ballRadius + arcOffset));
      }

      ball.add(createArcMesh(papToTangentPoints, 0xffffff));

      const vLT = new THREE.Vector3()
        .crossVectors(axisLT, lightningDir)
        .normalize();

      const axisPerp = vLT.clone();
      const angleSweepPerp = 2 * (sideLength / ballRadius);

      const perpArcPoints = generateArc({
        center: lightningPoint.clone(),
        axis: axisPerp,
        radius: ballRadius,
        angleSweep: angleSweepPerp,
        segments: 128,
        handednessFactor,
        arcOffset,
      });

      ball.add(createArcMesh(perpArcPoints, 0xffffff));

      const holeRadius = 0.5;
      const offsetAlongGreen = 0.15 + holeRadius;
      const offsetAngle = offsetAlongGreen / ballRadius;

      const holeCenterDir1 = lightningDir
        .clone()
        .applyAxisAngle(axisLT, offsetAngle);
      const holeCenter1 = holeCenterDir1
        .clone()
        .multiplyScalar(ballRadius + arcOffset);

      const holeCenterDir2 = lightningDir
        .clone()
        .applyAxisAngle(axisLT, -offsetAngle);
      const holeCenter2 = holeCenterDir2
        .clone()
        .multiplyScalar(ballRadius + arcOffset);

      drawFingerRing(
        ball,
        holeCenter1,
        holeRadius,
        ballRadius,
        arcOffset,
        0xffffff,
      );
      drawFingerRing(
        ball,
        holeCenter2,
        holeRadius,
        ballRadius,
        arcOffset,
        0xffffff,
      );

      gripCenter = lightningPoint ? lightningPoint.clone() : null;
    }
  }

  if (gripCenter && cameraPosition) {
    const fromCenterToGrip = gripCenter.clone().normalize();
    const fromCenterToCamera = new THREE.Vector3()
      .fromArray(cameraPosition)
      .normalize();
    if (
      fromCenterToGrip.lengthSq() > 1e-8 &&
      fromCenterToCamera.lengthSq() > 1e-8
    ) {
      const rotationQuat = new THREE.Quaternion().setFromUnitVectors(
        fromCenterToGrip,
        fromCenterToCamera,
      );
      ball.quaternion.premultiply(rotationQuat);

      if (gripHorizontalAxis) {
        const verticalDir = gripHorizontalAxis.clone().normalize();
        if (verticalDir.lengthSq() > 1e-8) {
          const camForward = fromCenterToCamera.clone();
          const rotatedVertical = verticalDir
            .clone()
            .applyQuaternion(ball.quaternion);
          const projectedUp = rotatedVertical
            .clone()
            .projectOnPlane(camForward)
            .normalize();
          if (projectedUp.lengthSq() > 1e-8) {
            const screenUp = new THREE.Vector3(0, 1, 0);
            const angle = projectedUp.angleTo(screenUp);
            const cross = new THREE.Vector3().crossVectors(
              projectedUp,
              screenUp,
            );
            const sign = Math.sign(cross.dot(camForward));
            const rollQuat = new THREE.Quaternion().setFromAxisAngle(
              camForward,
              angle * sign,
            );
            ball.quaternion.premultiply(rollQuat);
          }
        }
      }
    }
  }

  return (
    <group ref={ref} position={[0, 0, 0]}>
      <primitive object={ball} />
    </group>
  );
});
